import {
    Alert,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Box,
    Fade,
    Link,
    Tabs,
    Tab,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { When } from 'react-if';
import { LoadingButton } from '@mui/lab';
import { Replay, TableRows } from '@mui/icons-material';
import { useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';

import { Page } from '../../../../components';
import { CustomerTypes } from '../../../../types';
import { pomMasterSettingsFormSchema } from '../../../../schemas/settings.schema';
import { useGenericQuery } from '../../../../services/react-query/generic.query';
import { useGenericMutation } from '../../../../services/react-query/generic.mutation';
import { handleApiError } from '../../../../services/react-query/react-query.client';
import { MySubscription } from '../../../../components/subscription-card';
import AreYouSureModal from '../../../../components/are-you-sure-modal';
import { CustomerInvitesModal } from '../../../../components/customer-invites-modal';

import { TabPanel } from './components/tab-panel';
import { GeneralSettingsTab } from './components/general-settings-tab';
import { FeaturesSettingsTab } from './components/features-settings-tab';
import { SosSettingsTab } from './components/sos-settings-tab';
import { CheckOnMeSettingsTab } from './components/check-on-me-settings-tab';
import { ShiftSettingsTab } from './components/shift-settings-tab';
import { MapLocationSettingsTab } from './components/map-location-settings-tab';
import { DataRetentionSettingsTab } from './components/data-retention-settings-tab';
import { SsoSettingsTab } from './components/sso-settings-tab';
import { MobileAppSettingsTab } from './components/mobile-app-settings-tab';
import { RapidSosRegistryTab } from './components/rapidsos-registry-tab';

/**
 * The outer "Customer Details" / "RapidSOS" tabs. Separate from `TabPanel`
 * (used by the inner settings tabs below) so the two ids never collide, and
 * separate from `activeTab`/`FIELD_TO_TAB` so validation-error tab-jumping
 * for the settings form can't accidentally fight the outer tab selection.
 */
const OuterTabPanel: React.FC<{ value: number; index: number; children: React.ReactNode }> = ({
    value,
    index,
    children,
}) => (
    <Box
        role="tabpanel"
        id={`customer-outer-tabpanel-${index}`}
        aria-labelledby={`customer-outer-tab-${index}`}
        sx={{ display: value === index ? 'block' : 'none' }}
    >
        {children}
    </Box>
);

const OUTER_TAB_QUERY_PARAM = 'tab';
const OUTER_TAB_RAPIDSOS_VALUE = 'rapidsos';

/**
 * The only fields this screen still writes. Everything else it renders is a
 * v2 catalog setting: `Customer.settingsV2` is the source of truth and
 * `Customer.settings` is a derived mirror of it, so v1 settings are edited in
 * POM Master → Settings (v2) and shown here read-only.
 *
 * These ten are NOT catalog settings — they live on the Customer document
 * itself (name/contact/address/passCode) or under `Customer.settings` without a
 * canonical catalog name (phoneOwnership/phoneOperationSystems). They have no
 * v2 editor, so this screen stays their only editor and they must keep
 * submitting. The pom-2-api handler `$set`s name/contact/address/passCode
 * unconditionally, so all ten have to stay in the payload or a save would null
 * them out.
 */
const CUSTOMER_DETAIL_FIELDS = [
    'companyName',
    'contactFirstName',
    'contactLastName',
    'streetAddress',
    'cityAddress',
    'stateAddress',
    'zipcodeAddress',
    'phoneOwnership',
    'phoneOperationSystems',
    'passCode',
] as const;

/**
 * Validate only what can still be edited. Validating the full settings schema
 * would let a legacy org whose stored settings violate it (an unformatted
 * dispatch phone number, say) block a company-name save with an error the user
 * has no editable field to fix.
 */
const customerDetailFormSchema = pomMasterSettingsFormSchema.pick([...CUSTOMER_DETAIL_FIELDS]);

/**
 * Only the still-editable fields need a tab to jump to on a validation error;
 * the settings fields are read-only and cannot produce one.
 */
const FIELD_TO_TAB: Record<string, number> = {
    companyName: 0, contactFirstName: 0, contactLastName: 0,
    streetAddress: 0, cityAddress: 0, stateAddress: 0, zipcodeAddress: 0,
    phoneOwnership: 0, phoneOperationSystems: 0,
    passCode: 2,
};

const parseOptionalNumber = (value: unknown): number | undefined => {
    if (value === '' || value === null || value === undefined) {
        return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Narrow the form values down to the fields this screen still owns. Settings
 * fields are deliberately dropped rather than echoed back: every catalog field
 * omitted from the request is left untouched by the pom-2-api handler (each one
 * is guarded by `!== undefined`), so a save from this screen provably cannot
 * write `Customer.settings` or a `settingsV2` override.
 */
const buildCustomerDetailPayload = (
    values: CustomerTypes.OnboardingForm
): Partial<CustomerTypes.OnboardingForm> => {
    const payload: Record<string, unknown> = {};
    for (const field of CUSTOMER_DETAIL_FIELDS) {
        payload[field] =
            field === 'zipcodeAddress'
                ? parseOptionalNumber(values.zipcodeAddress)
                : values[field];
    }
    return payload as Partial<CustomerTypes.OnboardingForm>;
};

export const EditCustomerSettingsScreen = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [resendInviteModal, setResendInviteModal] = useState(false);
    const [invitesModal, setInvitesModal] = useState(false);
    const { id } = useParams();

    // Outer "Customer Details" / "RapidSOS" tab. Kept independent of
    // `activeTab` (the inner settings tabs) and sourced from the URL so the
    // RapidSOS tab is directly linkable via `?tab=rapidsos`.
    const [searchParams, setSearchParams] = useSearchParams();
    const outerTab = searchParams.get(OUTER_TAB_QUERY_PARAM) === OUTER_TAB_RAPIDSOS_VALUE ? 1 : 0;
    const handleOuterTabChange = (_: React.SyntheticEvent, newValue: number) => {
        const nextParams = new URLSearchParams(searchParams);
        if (newValue === 1) {
            nextParams.set(OUTER_TAB_QUERY_PARAM, OUTER_TAB_RAPIDSOS_VALUE);
        } else {
            nextParams.delete(OUTER_TAB_QUERY_PARAM);
        }
        setSearchParams(nextParams, { replace: true });
    };

    const customerDetailsUrl = id ? `customer/details?id=${id}` : 'customer/details';
    // cacheTime: 0 + staleTime: 0 — evict on unmount and refetch on mount.
    // POM masters switch customers frequently; we never want a stale-while-
    // revalidate flash of the previous customer's data while the fresh GET
    // is in flight.
    const { data: customerDetails, refetch: refetchCustomerDetails } = useGenericQuery<CustomerTypes.GetCustomerDetailsResponse>(
        'getCustomerDetails',
        customerDetailsUrl,
        undefined,
        0,
        false,
        0,
    );
    const selectedCustomer = customerDetails?.customer ?? null;
    const selectedCustomerSub = customerDetails?.subscriptions ?? null;

    const {
        refetch,
        data: settings,
        isLoading,
        isFetching,
    } = useGenericQuery<CustomerTypes.OnboardingForm>(
        'getCustomerSettings',
        `customer/getSettings?id=${id}`,
        undefined,
        0,
        false,
        0,
    );

    const { mutateAsync: updateSettings, isLoading: isUpdateLoading } =
        useGenericMutation<Partial<CustomerTypes.OnboardingForm>, boolean>(
            'updateSettings',
            `customer/updateSettings/${id}`,
            'put'
        );

    const { mutateAsync: resendInvite, isLoading: resendInviteLoading } =
        useGenericMutation<{ customerId: string; inviteId: string }, boolean>(
            'resendInvite',
            'customer/resendInvite',
            'post'
        );

    const formik = useFormik<CustomerTypes.OnboardingForm>({
        initialValues: settings
            ? { ...settings, ssoConfig: settings.ssoConfig ?? { active: false, clientId: '', ssoUrl: '' } }
            : ({} as CustomerTypes.OnboardingForm),
        validationSchema: customerDetailFormSchema,
        onSubmit: async (values) => {
            let handledByOnError = false;
            const payload = buildCustomerDetailPayload(values);

            try {
                await updateSettings(payload, {
                    onError: (e) => {
                        handledByOnError = true;
                        const msg = handleApiError(e);
                        toast(msg, { autoClose: 1500, type: 'error' });
                    },
                    onSuccess: async () => {
                        await refetch();
                        // Feature-flag visibility gates read useEffectiveSettings
                        // (GET /v2/settings/me); its react-query cache refreshes on
                        // window focus, so no redux sync is needed here anymore.
                        toast('Customer details successfully updated.', { autoClose: 1500, type: 'success' });
                        setHasUnsavedChanges(false);
                    },
                });
            } catch (e) {
                if (!handledByOnError) {
                    const msg = handleApiError(e);
                    toast(msg, { autoClose: 1500, type: 'error' });
                }
            }
        },
    });

    const { setValues, submitForm, validateForm, values } = formik;

    // Track unsaved changes. Scoped to the fields this screen still submits —
    // the settings fields are read-only, so a diff there could only ever come
    // from a render-time default and would strand the Save bar on screen.
    useEffect(() => {
        if (settings) {
            const hasChanges = CUSTOMER_DETAIL_FIELDS.some(
                (field) => JSON.stringify(values[field]) !== JSON.stringify(settings[field])
            );
            setHasUnsavedChanges(hasChanges);
        }
    }, [values, settings]);

    useEffect(() => {
        if (settings) {
            setValues({
                ...settings,
                ssoConfig: settings.ssoConfig ?? { active: false, clientId: '', ssoUrl: '' },
            });
        }
    }, [settings]);

    const handleSave = async () => {
        // Validate fresh instead of reading the stale `errors` snapshot:
        // submitForm() re-validates internally and silently refuses to call
        // onSubmit when invalid, so gating on this result is the only way a
        // blocked save always surfaces feedback.
        const validationErrors = await validateForm();
        const errs = Object.keys(validationErrors);
        if (errs.length > 0) {
            const errorMessages = Object.values(validationErrors)
                .map((e) => (typeof e === 'string' ? e : JSON.stringify(e)))
                .join('\n');
            toast(errorMessages, { autoClose: 1500, type: 'error' });

            // Auto-switch to the first tab with an error
            const firstErrorTab = errs.reduce((minTab, field) => {
                const tab = FIELD_TO_TAB[field];
                return tab !== undefined && tab < minTab ? tab : minTab;
            }, Infinity);
            if (firstErrorTab !== Infinity) {
                setActiveTab(firstErrorTab);
            }
        } else {
            await submitForm();
        }
    };

    return (
        <Page title={'POM Safe - Settings'}>
            {/* Floating Save/Cancel Bar */}
            <Fade in={hasUnsavedChanges} timeout={{ enter: 400, exit: 400 }}>
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 75,
                        zIndex: 9999,
                        py: 1,
                        px: 2,
                    }}
                >
                    <Container maxWidth="md" sx={{ mt: 1 }}>
                        <Grid container gap={2}>
                            <LoadingButton
                                onClick={handleSave}
                                loading={isUpdateLoading}
                                disabled={isUpdateLoading}
                                sx={{ fontWeight: 600, height: 40 }}
                                size="large"
                                variant="contained"
                            >
                                Save Changes
                            </LoadingButton>
                            <Button
                                variant="contained"
                                color="inherit"
                                size="large"
                                sx={{ mr: 1, height: 40 }}
                                onClick={() => {
                                    if (settings) {
                                        setValues({
                                            ...settings,
                                            ssoConfig: settings.ssoConfig ?? { active: false, clientId: '', ssoUrl: '' },
                                        });
                                    }
                                    setHasUnsavedChanges(false);
                                }}
                                disabled={isUpdateLoading}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    </Container>
                </Box>
            </Fade>

            <When condition={!isLoading && !isFetching}>
                <Container maxWidth={'xl'}>
                    {/* Header: Customer name + invite actions */}
                    <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                        mt={-5}
                        mb={2}
                    >
                        <Typography fontWeight={600} fontSize={22}>
                            {selectedCustomer?.name || 'Customer Settings'}
                        </Typography>
                        <Grid item>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Replay />}
                                sx={{ mr: 1 }}
                                onClick={() => setResendInviteModal(true)}
                                disabled={!selectedCustomer?.invitation}
                            >
                                Resend Onboarding Invite
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<TableRows />}
                                onClick={() => setInvitesModal(true)}
                                disabled={!selectedCustomer}
                            >
                                View Onboarding Invites
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Outer Customer Details / RapidSOS tabs */}
                    <Tabs
                        value={outerTab}
                        onChange={handleOuterTabChange}
                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    >
                        <Tab label="Customer Details" />
                        <Tab label="RapidSOS" />
                    </Tabs>

                    <OuterTabPanel value={outerTab} index={0}>
                        {/* Subscription Cards */}
                        <Grid item xs={12} md={12}>
                            <Grid container spacing={2}>
                                {selectedCustomer &&
                                    selectedCustomerSub?.map((sub) => (
                                        <Grid item xs={12} md={12} key={sub.id}>
                                            <MySubscription
                                                subscription={sub}
                                                customer={selectedCustomer}
                                                onSubscriptionChanged={() => { void refetchCustomerDetails(); }}
                                            />
                                        </Grid>
                                    ))}
                            </Grid>
                        </Grid>

                        {/* Settings Card with Tabs */}
                        <Card sx={{ mt: 2 }}>
                            <CardContent>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Settings are now managed in{' '}
                                    <Link component={RouterLink} to="/pom-master/settings">
                                        Settings (v2)
                                    </Link>
                                    . The settings below are read-only. Company, contact and
                                    address details and the SOS passcode can still be edited here.
                                </Alert>

                                <Tabs
                                    value={activeTab}
                                    onChange={(_, v) => setActiveTab(v)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                                >
                                    <Tab label="General" />
                                    <Tab label="Features" />
                                    <Tab label="SOS" />
                                    <Tab label="Check On Me" />
                                    <Tab label="Shift" />
                                    <Tab label="Map & Location" />
                                    <Tab label="Data Retention" />
                                    <Tab label="SSO" />
                                    <Tab label="Mobile App" />
                                </Tabs>

                                {/* Every settings tab is read-only; General and SOS keep
                                    their non-catalog fields (company/contact/address and
                                    the SOS passcode) editable. SSO was already
                                    read-only. */}
                                <TabPanel value={activeTab} index={0}>
                                    <GeneralSettingsTab formik={formik} settingsDisabled />
                                </TabPanel>
                                <TabPanel value={activeTab} index={1}>
                                    <FeaturesSettingsTab formik={formik} disabled />
                                </TabPanel>
                                <TabPanel value={activeTab} index={2}>
                                    <SosSettingsTab formik={formik} settingsDisabled />
                                </TabPanel>
                                <TabPanel value={activeTab} index={3}>
                                    <CheckOnMeSettingsTab formik={formik} disabled />
                                </TabPanel>
                                <TabPanel value={activeTab} index={4}>
                                    <ShiftSettingsTab formik={formik} disabled />
                                </TabPanel>
                                <TabPanel value={activeTab} index={5}>
                                    <MapLocationSettingsTab formik={formik} disabled />
                                </TabPanel>
                                <TabPanel value={activeTab} index={6}>
                                    <DataRetentionSettingsTab formik={formik} disabled />
                                </TabPanel>
                                <TabPanel value={activeTab} index={7}>
                                    <SsoSettingsTab formik={formik} />
                                </TabPanel>
                                <TabPanel value={activeTab} index={8}>
                                    <MobileAppSettingsTab formik={formik} disabled />
                                </TabPanel>
                            </CardContent>
                        </Card>
                    </OuterTabPanel>

                    <OuterTabPanel value={outerTab} index={1}>
                        <RapidSosRegistryTab
                            active={outerTab === 1}
                            customerId={id}
                            customerName={selectedCustomer?.name}
                        />
                    </OuterTabPanel>
                </Container>
            </When>

            {/* Resend Invite Modal */}
            <AreYouSureModal
                visible={resendInviteModal}
                title="Resend Onboarding Invite"
                description="Are you sure you want to resend the onboarding invite to this customer?"
                onHide={() => setResendInviteModal(false)}
                onConfirm={async () => {
                    let handledByOnError = false;
                    try {
                        await resendInvite(
                            { customerId: selectedCustomer?.id!, inviteId: selectedCustomer?.invitation?.id! },
                            {
                                onError: (e) => {
                                    handledByOnError = true;
                                    const msg = handleApiError(e);
                                    toast(msg, { autoClose: 1500, type: 'error' });
                                },
                                onSuccess: async () => {
                                    toast('Invite resent successfully.', { autoClose: 1500, type: 'success' });
                                },
                            }
                        );
                    } catch (e) {
                        if (!handledByOnError) {
                            const msg = handleApiError(e);
                            toast(msg, { autoClose: 1500, type: 'error' });
                        }
                    }
                    setResendInviteModal(false);
                }}
                color={'primary'}
                loading={resendInviteLoading}
            />

            {/* View Invites Modal */}
            {invitesModal && selectedCustomer && (
                <CustomerInvitesModal
                    visible={invitesModal}
                    customer={selectedCustomer}
                    onClose={() => setInvitesModal(false)}
                />
            )}
        </Page>
    );
};
